import { Button, Checkbox, FormControlLabel, Icon, IconButton, makeStyles, Paper, Theme } from '@material-ui/core';
import { capitalizeAll, capitalizeFirst, ModeSelectInputField, ModeTextInputField, SelectInputOption, useModeFormStyle }
    from '@moderepo/mode-app-components';
import clsx from 'clsx';
import produce, { castDraft, Draft } from 'immer';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { ThresholdComparisonOperator, TimeSeriesAggregation, AlertConditionType, AlertSeverity, MetricsThresholdAlertCondition, AlertTargetMetric,
    isMetricsHeartbeatAlertCondition, isMetricsThresholdAlertCondition, MetricsAlertConditionThreshold, MetricsHeartbeatAlertCondition,
    EntityMetricsDefinition, NumericMetricInfo, MetricType } from '@moderepo/mode-apis';
import { Trans } from 'react-i18next';
import { AlertRuleEditorStep, ALERT_RULE_CONDITION_DEFAULT_AGGREGATION, ALERT_RULE_CONDITION_DEFAULT_DELAY, ALERT_RULE_CONDITION_DEFAULT_DURATION,
    ALERT_RULE_CONDITION_DEFAULT_FREQUENCY, ALERT_RULE_CONDITION_DEFAULT_SEVERITY, ALERT_RULE_CONDITION_THRESHOLD_OPERAND_MAX,
    createAlertConditionDelayOptions, createTimeSeriesAggregationOptions, useEditAlertRuleStyle } from '.';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';
import { createSeverityOptions, createThresholdComparisonOperatorOptions, createAlertConditionDurationOptions } from './utils';
import { getAlertSeverityDisplayName } from '..';
import { useSelectTranslatedEntityClasses } from '../..';


const useStyle = makeStyles((theme: Theme) => {
    return {

        formControlGroupBasicAttribute: {
            display      : 'flex',
            alignItems   : 'center',
            flexDirection: 'column',
            padding      : 0,
        },

        basicAttributeInput: {
            width         : '100%',
            flex          : 1,
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'flex-start',

            '& .label': {
                fontWeight: 'bold',
                fontSize  : 'larger',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',
                '& .label'   : {
                    marginRight: theme.spacing(2),
                },

                '& .select-input': {
                    minWidth: '10em',
                    width   : 'auto',
                },
            },
        },


        thresholdInputFormGroup: {
            width        : '100%',
            flex         : 1,
            display      : 'flex',
            flexDirection: 'column',
        },

        thresholdInputsContainer: {
            width        : '100%',
            flex         : 1,
            display      : 'flex',
            alignItems   : 'flex-start',
            flexDirection: 'row',
            flexWrap     : 'wrap',

            '&:not(:last-child)': {
                marginBottom: theme.spacing(2),
            },

            '& .form-control': {
                width       : '100%',
                '&.operator': {
                    minWidth: '10em',
                },
                '&.operand': {
                    minWidth: '5em',
                },
            },

            '& .conjunction': {
                width: '100%',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',

                '& .form-control': {
                    width               : 'min-content',
                    '&:not(:last-child)': {
                        marginRight: theme.spacing(1),
                    },
                },

                '& .conjunction': {
                    width      : 'min-content',
                    marginTop  : 20,
                    marginRight: theme.spacing(1),
                    whiteSpace : 'nowrap',
                },

                '& .add-threshold-button': {
                    marginTop: 20,
                },
            },
        },

        thresholdSettingsTitle: {
            width        : '100%',
            textAlign    : 'left',
            fontWeight   : 'bold',
            paddingBottom: theme.spacing(1),

            '& .severity': {
                borderRadius: 20,
                padding     : '2px 7px',
            },

            [`& .severity-${AlertSeverity.LOW}`]: {
                color     : theme.palette.info.contrastText,
                background: theme.palette.info.main,
            },
            [`& .severity-${AlertSeverity.MODERATE}`]: {
                color     : theme.palette.warning.contrastText,
                background: theme.palette.warning.main,
            },
            [`& .severity-${AlertSeverity.HIGH}`]: {
                color     : theme.palette.error.contrastText,
                background: theme.palette.error.main,
            },
        },

        thresholdSettingsError: {
            width    : '100%',
            textAlign: 'left',
            color    : theme.palette.error.main,
        },

        emptyThresholdError: {
            color    : theme.palette.error.main,
            width    : '100%',
            flex     : 1,
            textAlign: 'center',
            margin   : theme.spacing(2, 0),
            fontSize : 'larger',
        },

    };
}, { name: 'EditAlertRuleStepMetricsCondition', index: 1 });


/**
 * Get the array of alert severity values from the AlertSeverity enum. We will need to loop through this array in a few places
 * so we will create this variable here and use it later instead of recreating this many times.
 */
const alertSeverities: readonly AlertSeverity[] = [AlertSeverity.HIGH, AlertSeverity.MODERATE, AlertSeverity.LOW];


/**
 * The number of operands required are different for different type of comparison operator. Some operator requires 1 operand, some operator
 * requires 2 operands, and some can have unlimited # of operands.
 * We will create this map of the the operator => # of operands so that we can re-use it.
 * Note: value 0 means unlimited.
 */
const operandsLengthByOperatorType: {[operator: string]: number} = {
    [ThresholdComparisonOperator.LESS_THAN]               : 1,
    [ThresholdComparisonOperator.LESS_THAN_OR_EQUAL_TO]   : 1,
    [ThresholdComparisonOperator.GREATER_THAN_OR_EQUAL_TO]: 1,
    [ThresholdComparisonOperator.GREATER_THAN]            : 1,
    [ThresholdComparisonOperator.IN]                      : 0,
    [ThresholdComparisonOperator.NOT_IN]                  : 0,
    [ThresholdComparisonOperator.BETWEEN]                 : 2,
    [ThresholdComparisonOperator.OUTSIDE]                 : 2,
};


/**
 * The operand inputs has labels. By default, we will use this label "Value" but sometime we want to use different labels so this
 * is a map that tell the UI which labels to use for which operator.
 * For example, for "Between" and "Outside" operators, it is better to use the labels "From" and "To" for the 2 operands instead of "Values"
 */
const operandLabelsByOperatorType: {[operator: string]: readonly string[]} = {
    [ThresholdComparisonOperator.BETWEEN]: ['label.from', 'label.to'],
    [ThresholdComparisonOperator.OUTSIDE]: ['label.from', 'label.to'],
};

/**
 * Given an operator and the index of the operand. Return the custom labels created for that operator.
 * Return 'label.value' if there is no custom label.
 */
const getOperandLabelsByOperatorType = (operator: ThresholdComparisonOperator | undefined, index: number): string => {
    if (operator && operandLabelsByOperatorType[operator]?.[index] !== undefined) {
        return operandLabelsByOperatorType[operator][index];
    }
    return 'label.value';
};


/**
 * When there are more than 1 operands, sometime we want to show some conjunction between each operands but we don't want to do it it for ALL
 * cases. For example, we want to show "or" in between each operand for the "In" and "Not In" operators so that the operand inputs will show up
 * like this
 *      "Equal to Value or Value or Value ...
 *      "Not Equal to Value and Value and Value ...
 */
const operandConjunctionByOperatorType: {[operator: string]: string} = {
    [ThresholdComparisonOperator.IN]    : 'label.or',
    [ThresholdComparisonOperator.NOT_IN]: 'label.and',
};


/**
 * Given an operator, return the custom conjunction created for that operator.
 * Return '' if there is no custom conjunction.
 */
const getOperandsConjunctionByOperatorType = (operator: ThresholdComparisonOperator | undefined): string => {
    if (operator && operandConjunctionByOperatorType[operator] !== undefined) {
        return operandConjunctionByOperatorType[operator];
    }
    return '';
};



/**
 * The structure for the Condition Step errors, This interface should kind of match the interface for the inputs but doesn't have to be
 * exactly the same.
 */
interface StepConditionInputErrors {
    duration?: string | undefined;
    delay?: string | undefined;
    frequency?: string | undefined;
    severity?: string | undefined;
    aggregation?: string | undefined;

    thresholds?: {
        [severity: number]: string | undefined;
    } | undefined;
}


/**
 * The interfaces for storing the user's alert condition inputs. Unlike the actual condition values, everything in this interface can be
 * undefined if the user has not provided a value for them.
 */
export interface AlertConditionThresholdInputs {
    readonly severity: AlertSeverity;
    readonly operator?: ThresholdComparisonOperator | undefined;
    readonly operands?: (readonly (number | string | undefined)[]) | undefined;
}


export interface AlertConditionInputs {
    readonly duration?: string | undefined;
    readonly delay?: string | undefined;
    readonly frequency?: string | undefined;
    readonly severity?: AlertSeverity | undefined;
    readonly aggregation?: TimeSeriesAggregation | undefined;

    /**
     * Thresholds is supposed to be an Array but this array can only have maximum 3 thresholds, 1 for each severity, and the severity
     * has to be unique. We can't have multiple threshold setting for the same severity.
     * Therefore, instead of using Arrays, we will be using a MAP instead for storing the inputs. The key for this map will be the severity.
     * This will make it easier to make sure the user can't create more than 3 thresholds and also easier to make sure the thresholds' severity
     * are unique. This is how we store the threshold inputs
     *
     *      // Note: severity values are numerical but the example will use string just to make it easy to understand
     *      thresholds = {
     *          high: {severity: "high", operator": "<", operands: [1]},
     *          moderate: {severity: "moderate", operator": ">", operands: [1]},
     *          low: {severity: "low", operator": "===", operands: [1, 2, 3]},
     *      }
     */
    readonly thresholds?: {
        [severity: number]: AlertConditionThresholdInputs;
    } | undefined;
}


/**
 * Helper function to create a new operands array.
 *
 * Given an operator, create and return an Array of numbers to be used for storing the operands for that operator. If prevOperands is
 * provided, initialize the new array with the values from that prevOperands. This way we can keep the prev operand values when the
 * user change operator.
 *
 * Different operator will require different number of operands. Some operators require 1 operand, some require 2 operands, and some
 * can have unlimited operands.
 */
const createOperandsArray = (
    operator: ThresholdComparisonOperator,
    prevOperands?: readonly (number | string | undefined)[] | undefined,
): readonly (number | string | undefined)[] => {

    // Get the number of operands required based on the operator type. NOTE: this value can be 0 which mean the operators can have
    // unlimited # of operands but will require minimum 1 operand.
    const numberOfOperands = operandsLengthByOperatorType[operator];

    if (prevOperands) {
        // If prevOperand is provided. Create a NEW Array of size "numberOfOperands". If "numberOfOperands" is 0 which mean the # of operands is
        // unlimited then we will create a new array of the same size as the prevOperand so that we can keep all the values from prevOperand.
        const newOperands = (new Array<number | string | undefined>(numberOfOperands || prevOperands.length || 1)).fill(undefined);

        // Copy values from prevOperands to the newOperands
        newOperands.forEach((_, index) => {
            newOperands[index] = prevOperands[index];
        });
        return newOperands;
    }

    // No prevOperands to copy values from so just return a NEW array of numbers of size numberOfOperands and fill it with undefined.
    return (new Array<number | undefined>(numberOfOperands || 1)).fill(undefined);
};


// Because this Step only have the UI to configure the alert condition attributes excluding metricsCondition, we have to create a Sub interface
// for the MetricsHeartbeatAlertCondition and MetricsThresholdAlertCondition that exclude the "metricsCondition"
// When this step is completed, these are the objects that this step will pass to the parent container and these objects won't have
// metricsCondition. We can't use MetricsHeartbeatAlertCondition because then we will have to pass something for the "metricsCondition"
export interface SubMetricsHeartbeatAlertCondition extends Omit<MetricsHeartbeatAlertCondition, 'metricsCondition'> {}
export interface SubMetricsThresholdAlertCondition extends Omit<MetricsThresholdAlertCondition, 'metricsCondition'> {}



/**
 * Helper function to convert an existing alert conditions to AlertConditionInputs object. This is when this component is used for
 * editing an existing alert condition.
 *
 * @param existingCondition - The alert condition for an existing Alert Rule
 * @param conditionType - For create new alert mode, there won't be any existing condition therefore, we will need the conditionType to know how to
 *                          set up the condition inputs.
 */
const metricsConditionToInputs = (
    existingCondition: SubMetricsHeartbeatAlertCondition | SubMetricsThresholdAlertCondition | undefined,
    conditionType: AlertConditionType,
): AlertConditionInputs => {

    // If there is existing condition, initialize the inputs with the existing condition
    if (existingCondition) {
        return {
            delay      : existingCondition.delay ?? ALERT_RULE_CONDITION_DEFAULT_DELAY,
            duration   : existingCondition.duration ?? ALERT_RULE_CONDITION_DEFAULT_DURATION,
            frequency  : existingCondition.frequency ?? ALERT_RULE_CONDITION_DEFAULT_FREQUENCY,
            severity   : isMetricsHeartbeatAlertCondition(existingCondition) ? existingCondition.severity : undefined,
            aggregation: isMetricsThresholdAlertCondition(existingCondition)
                ? existingCondition.aggregation ?? ALERT_RULE_CONDITION_DEFAULT_AGGREGATION : undefined,
            thresholds: isMetricsThresholdAlertCondition(existingCondition) ? existingCondition.thresholds.reduce((thresholdInputs, threshold) => {
                // eslint-disable-next-line no-param-reassign
                thresholdInputs[threshold.severity] = {
                    severity: threshold.severity ?? {},
                    operator: threshold.operator,
                    operands: createOperandsArray(threshold.operator, threshold.operands),
                };
                return thresholdInputs;
            }, {}) : undefined,
        };
    }

    // There is no existing condition, this means the user is in a CREATE condition mode. Return a new condition inputs object with
    // all default values.
    return {
        delay      : ALERT_RULE_CONDITION_DEFAULT_DELAY,
        duration   : ALERT_RULE_CONDITION_DEFAULT_DURATION,
        frequency  : ALERT_RULE_CONDITION_DEFAULT_FREQUENCY,
        severity   : conditionType === AlertConditionType.METRIC_HEARTBEAT ? ALERT_RULE_CONDITION_DEFAULT_SEVERITY : undefined,
        aggregation: conditionType === AlertConditionType.METRIC_THRESHOLD ? ALERT_RULE_CONDITION_DEFAULT_AGGREGATION : undefined,
        thresholds : conditionType === AlertConditionType.METRIC_THRESHOLD ? {
            [AlertSeverity.HIGH]: {
                severity: AlertSeverity.HIGH,
            },
        } : undefined,
    };
};



/**
 * The required props for the EditAlertRuleStepMetricsCondition in addition to the props inherited from AlertRuleEditorStep
 */
export interface EditAlertRuleStepMetricsConditionProps extends AlertRuleEditorStep {
    readonly alertRuleTargetMetric?: AlertTargetMetric | undefined;
    readonly alertRuleCondition?: SubMetricsHeartbeatAlertCondition | SubMetricsThresholdAlertCondition | undefined;
    readonly onStepCompleted: (alertCondition: SubMetricsHeartbeatAlertCondition | SubMetricsThresholdAlertCondition)=> void;
}



/**
 * This is the Edit Alert Rule Condition step which is the step which the user needs to provide the condition for the alert rule e.g.
 * duration, severity, thresholds, etc...
 */
export const EditAlertRuleStepMetricsCondition: React.FC<EditAlertRuleStepMetricsConditionProps> = (
    props: EditAlertRuleStepMetricsConditionProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const sharedClasses = useEditAlertRuleStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();

    /**
     * Get the target metric's metricsDefinition so that we can determine if it is a 'derived' metric and show Delay input option.
     * We will also need the target metric's metric info so that we can get the metric's unit and display it.
     */
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const {
        targetMetricsDefinition,
        targetMetric,
    } = useMemo((): {
        readonly targetMetricsDefinition: EntityMetricsDefinition | undefined;
        readonly targetMetric: NumericMetricInfo | undefined;
    } => {
        // Get the targetMetric from the alert rule.
        const metricTarget = props.alertRuleTargetMetric;

        // Find the entityClass object based on the target.entityClass
        if (metricTarget && entityClasses) {
            const entityClass = entityClasses.find((entityClass) => {
                return entityClass.entityClass === metricTarget.entityClass;
            });

            // If entityClass object found, find the metricsDefinition and metric info based on the metricTarget's settings
            if (entityClass) {
                const metricsDefinition = entityClass.metricsDefinitions?.find((metricsDef) => {
                    return metricsDef.metricsDefinitionId === metricTarget.metricsDefinitionId;
                });
                const metricInfo = metricTarget.metricName ? metricsDefinition?.numericMetrics?.find((metric) => {
                    return metric.name === metricTarget.metricName;
                }) : undefined;

                return {
                    targetMetricsDefinition: metricsDefinition,
                    targetMetric           : metricInfo,
                };
            }
        }

        return {
            targetMetricsDefinition: undefined,
            targetMetric           : undefined,
        };
    }, [entityClasses, props.alertRuleTargetMetric]);


    // This is the state where all the user inputs for the condition are stored
    const [conditionInputs, setConditionInputs] = useState<AlertConditionInputs>(
        metricsConditionToInputs(props.alertRuleCondition, props.alertConditionType),
    );

    // The states for tracking the errors for each input
    const [inputErrors, setInputErrors] = useState<StepConditionInputErrors>({});
    const [emptyThresholdError, setEmptyThresholdError] = useState<boolean>(
        props.alertConditionType === AlertConditionType.METRIC_THRESHOLD
        && (conditionInputs.thresholds === undefined || Object.values(conditionInputs.thresholds).length === 0),
    );


    /**
     * Create and cache the options to be used for the Select inputs
     */
    const durationOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createAlertConditionDurationOptions(trans);
    }, [trans]);

    const delayOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createAlertConditionDelayOptions(trans);
    }, [trans]);

    const aggregationOptions = useMemo((): readonly SelectInputOption<TimeSeriesAggregation>[] => {
        return createTimeSeriesAggregationOptions(trans);
    }, [trans]);

    const severityOptions = useMemo((): readonly SelectInputOption<AlertSeverity>[] => {
        return createSeverityOptions(trans);
    }, [trans]);

    const thresholdComparisonOperatorOptions = useMemo((): readonly SelectInputOption<ThresholdComparisonOperator>[] => {
        return createThresholdComparisonOperatorOptions(trans);
    }, [trans]);



    /**
     * Add a threshold inputs for the specified severity.
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "<", operands: [5]}
     *          }
     *      }
     *
     * Severity = "high"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "<", operands: [5]},
     *              high: {severity:"high", operator: undefined, operands: undefined}
     *          }
     *      }
     */
    const addThreshold = useCallback((severity: AlertSeverity) => {
        props.onStepError(false);

        setConditionInputs((currentInputs) => {
            // Clear the empty threshold input errors since we are going to add one now
            setEmptyThresholdError(false);

            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                if (!draft.thresholds) {
                    draft.thresholds = {};
                }
                draft.thresholds[severity] = {
                    severity,
                    operator: undefined,
                    operands: undefined,
                };
            });
        });
    }, [props]);


    /**
     * Remove a threshold settings for the specified severity
     */
    const removeThreshold = useCallback((severity: AlertSeverity) => {
        props.onStepError(false);

        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                if (draft.thresholds) {
                    delete draft.thresholds[severity];

                    // If we deleted a threshold, it is possible that there won't be any threshold left. So we need to check and set the
                    // emptyThresholdError to true if there isn't any threshold left.
                    const hasThreshold = Object.values(draft.thresholds).length > 0;
                    if (!hasThreshold) {
                        setEmptyThresholdError(true);
                    }
                }
            });
        });
    }, [props]);


    /**
     * Change the duration of the condition inputs
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "1min",
     *          ...
     *      }
     *
     * duration="1hour"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "1hour",
     *          ...
     *      }
     */
    const setDuration = useCallback((value: string) => {
        // Input changed so clear the error
        props.onStepError(false);

        setInputErrors((currentInputErrors) => {
            return produce(currentInputErrors, (draft: StepConditionInputErrors) => {
                delete draft.duration;
            });
        });

        // Update the inputs state
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                draft.duration = value;
            });
        });
    }, [props]);


    /**
     * Change the delay of the condition inputs
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          delay: "1min",
     *          ...
     *      }
     *
     * delay="1hour"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          delay: "1hour",
     *          ...
     *      }
     */
    const setDelay = useCallback((value: string) => {
        // Input changed so clear the error
        props.onStepError(false);

        setInputErrors((currentInputErrors) => {
            return produce(currentInputErrors, (draft: StepConditionInputErrors) => {
                delete draft.delay;
            });
        });

        // Update the inputs state
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                draft.delay = value;
            });
        });
    }, [props]);


    /**
     * Change the severity of the condition inputs. This is the top level severity input which is used for HEARTBEAT condition type
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          severity: "low",
     *          ...
     *      }
     *
     * severity="high"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          severity: "high",
     *          ...
     *      }
     */
    const setSeverity = useCallback((value: AlertSeverity) => {
        // Input changed so clear the error
        props.onStepError(false);

        setInputErrors((currentInputErrors) => {
            return produce(currentInputErrors, (draft: StepConditionInputErrors) => {
                delete draft.severity;
            });
        });

        // Update the inputs state
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                draft.severity = value;
            });
        });
    }, [props]);


    /**
     * Change the aggregation of condition inputs
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          aggregation: "avg",
     *          ...
     *      }
     *
     * aggregation="min"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          aggregation: "min",
     *          ...
     *      }
     */
    const setAggregation = useCallback((value: TimeSeriesAggregation) => {
        // Input changed so clear the error
        props.onStepError(false);

        setInputErrors((currentInputErrors) => {
            return produce(currentInputErrors, (draft: StepConditionInputErrors) => {
                delete draft.aggregation;
            });
        });

        // Update the inputs state
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                draft.aggregation = value;
            });
        });
    }, [props]);


    /**
     * Change the operator of the threshold for the specified severity
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "<", operands: [5]}
     *          }
     *      }
     *
     * Severity = "low", Operator="between"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "between", operands: [5, undefined]},  // NOTE: operand changed as well
     *          }
     *      }
     */
    const setThresholdOperator = useCallback((severity: AlertSeverity, operator: ThresholdComparisonOperator) => {
        // Input changed so clear the error
        props.onStepError(false);

        setInputErrors((currentInputErrors) => {
            return produce(currentInputErrors, (draft: StepConditionInputErrors) => {
                delete draft.thresholds?.[severity];
            });
        });

        // Update the inputs state
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {

                // Make sure there is a threshold inputs for the specified severity
                const thresholdInputs = draft.thresholds?.[severity];
                if (thresholdInputs) {
                    thresholdInputs.operator = operator;

                    // Because we changed the operator, the operands array need to be changed as well because different operator require
                    // different number of operands
                    thresholdInputs.operands = castDraft(createOperandsArray(operator, thresholdInputs.operands));
                }
            });
        });
    }, [props]);


    /**
     * Add 1 more operand to the operands array belonged to the thresholds for the specified severity
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "<", operands: [5]}
     *          }
     *      }
     *
     * Severity = "low"
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "<", operands: [5, undefined]},
     *          }
     *      }
     */
    const increaseThresholdOperandsSize = useCallback((severity: AlertSeverity) => {
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                // Make sure there is a threshold inputs for the specified severity
                const thresholdInputs = draft.thresholds?.[severity];
                if (thresholdInputs) {
                    // Make a copy of the previous operands array and add 1 more item to the end
                    const newOperands = [...(thresholdInputs.operands ?? []), undefined];
                    thresholdInputs.operands = newOperands;
                }
            });
        });
    }, []);


    /**
     * Remove the last operand from the operands array in the threshold for the specified severity
     */
    const decreaseThresholdOperandsSize = useCallback((severity: AlertSeverity) => {
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {
                // Make sure there is a threshold inputs for the specified severity
                const thresholdInputs = draft.thresholds?.[severity];
                if (thresholdInputs) {
                    // Make a copy of the previous operand excluding the last operand
                    const newOperands = (thresholdInputs.operands ?? [])?.slice(0, -1);
                    thresholdInputs.operands = newOperands;
                }
            });
        });
    }, []);


    /**
     * Change the operand value of the threshold for the specified severity
     *
     * Current condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "between", operands: [5, 10]}
     *          }
     *      }
     *
     * Severity = "low", index:1, value:100
     *
     * New condition inputs:
     *      conditionInputs = {
     *          frequency: "...",
     *          duration: "...",
     *          threshold: {
     *              low: {severity:"low", operator: "between", operands: [5, 100]},
     *          }
     *      }
     */
    const setThresholdOperandValue = useCallback((severity: AlertSeverity, index: number, value: string) => {
        // Input changed so clear the error
        props.onStepError(false);

        setInputErrors((currentInputErrors) => {
            return produce(currentInputErrors, (draft: StepConditionInputErrors) => {
                delete draft.thresholds?.[severity];
            });
        });

        // Update the inputs state
        setConditionInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<AlertConditionInputs>) => {

                // Make sure there is a threshold inputs for the specified severity
                const thresholdInputs = draft.thresholds?.[severity];
                if (thresholdInputs && thresholdInputs.operands) {
                    thresholdInputs.operands[index] = value;
                }
            });
        });
    }, [props]);


    /**
     * Submit the user inputs for this step. Before submitting the inputs, we will validate the inputs. If there are no errors,
     * we will notify the parent container by calling ths 'onStepCompleted' callback and provide the parent container with the new
     * inputs and return 'true' for success. If there are errors, we will notify the parent container that there are errors but only
     * if 'dispatchOnError' is true and also return false for failure.
     */
    const submitInputs = useCallback((dispatchOnError: boolean = true): boolean => {

        const newInputErrors: StepConditionInputErrors = { };

        // Check if duration is provided
        if (!conditionInputs.duration) {
            newInputErrors.duration = 'error.input.generic.empty';
        }

        // Validate Severity input for METRICS_HEARTBEAT condition type
        if (props.alertConditionType === AlertConditionType.METRIC_HEARTBEAT && !conditionInputs.severity) {
            newInputErrors.severity = 'error.input.generic.empty';
        }

        // If condition type if METRICS_THRESHOLD, check threshold inputs
        if (props.alertConditionType === AlertConditionType.METRIC_THRESHOLD) {

            // Validate Aggregation input
            if (props.alertConditionType === AlertConditionType.METRIC_THRESHOLD && !conditionInputs.aggregation) {
                newInputErrors.aggregation = 'error.input.generic.empty';
            }

            const allThresholdsInputs = conditionInputs.thresholds;
            if (allThresholdsInputs) {
                // Create an object to store input errors for each threshold if this object does not exist
                if (!newInputErrors.thresholds) {
                    newInputErrors.thresholds = {};
                }

                const thresholdsInputsErrors = newInputErrors.thresholds;

                // Validate EACH Threshold inputs e.g. severity, operator, operands
                Object.entries(allThresholdsInputs).forEach(([severity, thresholdInputs]) => {
                    // Make sure all attributes are not undefined
                    if (!thresholdInputs.severity || !thresholdInputs.operator || !thresholdInputs.operands) {
                        thresholdsInputsErrors[severity] = 'alerting.edit_alert_rule.step_conditions.thresholds.errors.invalid';
                    }

                    // If operands is NOT undefined, make sure it contains valid values
                    if (thresholdInputs.operands) {
                        // Check if ALL operand has values
                        if (thresholdInputs.operands.some((value) => {
                            return value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0);
                        })) {
                            thresholdsInputsErrors[severity] = 'alerting.edit_alert_rule.step_conditions.thresholds.errors.invalid';
                        }

                        // Validate operand values for some special operators
                        const operator = thresholdInputs.operator;
                        if (!thresholdsInputsErrors[severity]) {
                            // For "between" and "outside" operators, the operands[0] < operands[1]
                            if (operator === ThresholdComparisonOperator.BETWEEN || operator === ThresholdComparisonOperator.OUTSIDE) {

                                if (thresholdInputs.operands[0] !== undefined && thresholdInputs.operands[1] !== undefined
                                && Number(thresholdInputs.operands[0]) >= Number(thresholdInputs.operands[1])) {
                                    // eslint-disable-next-line max-len
                                    thresholdsInputsErrors[severity] = 'alerting.edit_alert_rule.step_conditions.thresholds.operands.errors.from_to_invalid';
                                }
                            }

                            // For "in" and "not in" operators, all values must be unique
                            if (operator === ThresholdComparisonOperator.IN || operator === ThresholdComparisonOperator.NOT_IN) {
                                // Loop through the operands array and stop when we find 1 operand that is a duplicate
                                const uniqueValues = {};
                                const hasDuplicate = thresholdInputs.operands.some((value) => {
                                    if (value) {
                                        // check if this value already added to the uniqueValues map. If it is, return true, we found a duplicate
                                        if (uniqueValues[value]) {
                                            return true;
                                        }
                                        // If value does not exist, add it to the map
                                        uniqueValues[value] = true;
                                    }
                                    return false;
                                });
                                if (hasDuplicate) {
                                    // eslint-disable-next-line max-len
                                    thresholdsInputsErrors[severity] = 'alerting.edit_alert_rule.step_conditions.thresholds.operands.errors.duplicate_invalid';
                                }
                            }
                        }

                    }
                });
            }
        }

        // Update the local inputErrors
        setInputErrors(newInputErrors);



        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = emptyThresholdError || Object.entries(newInputErrors).some(([field, error]) => {
            if (field === 'thresholds') {
                return Object.values(error).some((error) => {
                    return error !== undefined;
                });
            }
            return error !== undefined;
        });


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        // If there are no errors, notify the parent container this step is completed
        if (!hasErrors) {
            if (props.alertConditionType === AlertConditionType.METRIC_HEARTBEAT) {
                props.onStepCompleted({
                    conditionType: AlertConditionType.METRIC_HEARTBEAT,
                    duration     : conditionInputs.duration as string,
                    delay        : conditionInputs.delay as string,
                    frequency    : conditionInputs.frequency as string,
                    severity     : conditionInputs.severity as AlertSeverity,
                } as SubMetricsHeartbeatAlertCondition);
            }

            if (props.alertConditionType === AlertConditionType.METRIC_THRESHOLD && conditionInputs.thresholds !== undefined) {
                const thresholds = Object.values(conditionInputs.thresholds);
                if (thresholds.length > 0) {
                    props.onStepCompleted({
                        conditionType: AlertConditionType.METRIC_THRESHOLD,
                        duration     : conditionInputs.duration as string,
                        delay        : conditionInputs.delay as string,
                        frequency    : conditionInputs.frequency as string,
                        aggregation  : conditionInputs.aggregation as TimeSeriesAggregation,
                        thresholds   : thresholds.map((inputs): MetricsAlertConditionThreshold => {
                            return {
                                severity: inputs.severity as AlertSeverity,
                                operator: inputs.operator as ThresholdComparisonOperator,
                                operands: inputs.operands?.map((operand) => {
                                    if (typeof operand === 'string') {
                                        return Number(operand);
                                    }
                                    return operand;
                                }) as [number, ...number[]],
                            };
                        }) as [MetricsAlertConditionThreshold, ...MetricsAlertConditionThreshold[]],
                    } as SubMetricsThresholdAlertCondition);
                }
            }
        }

        return !hasErrors;
    }, [conditionInputs.aggregation, conditionInputs.delay, conditionInputs.duration, conditionInputs.frequency,
        conditionInputs.severity, conditionInputs.thresholds, emptyThresholdError, props]);



    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('alerting.edit_alert_rule.step_conditions.subtitle')}
                </div>
                <div className={clsx(sharedClasses.formControlGroup, classes.formControlGroupBasicAttribute)}>
                    <div className={clsx(classes.basicAttributeInput)}>
                        <div className="label">{capitalizeFirst(trans(
                            props.alertConditionType === AlertConditionType.METRIC_HEARTBEAT
                                ? 'alerting.edit_alert_rule.label.heartbeat_condition_duration'
                                : 'alerting.edit_alert_rule.label.thresholds_condition_duration',
                        ))}
                        </div>
                        <ModeSelectInputField
                            className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                            fieldName="duration"
                            editable
                            isInEditMode
                            hideEditableIcon
                            value={conditionInputs.duration ?? ''}
                            error={inputErrors.duration ? trans(inputErrors.duration) : undefined}
                            options={durationOptions ?? []}
                            onInputChange={(fieldName, newValue) => {
                                setDuration(newValue);
                            }}
                        />
                    </div>

                    {/* If metricsDefinition's metricsType is 'derived' then we will give the user an option to set the delay */}
                    {targetMetricsDefinition?.metricsType === MetricType.DERIVED && (
                        <div className={clsx(classes.basicAttributeInput)}>
                            <div className="label">{capitalizeFirst(trans('alerting.edit_alert_rule.label.alert_condition_delay'))}
                            </div>
                            <ModeSelectInputField
                                className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                                fieldName="delay"
                                editable
                                isInEditMode
                                hideEditableIcon
                                value={conditionInputs.delay ?? ''}
                                error={inputErrors.delay ? trans(inputErrors.delay) : undefined}
                                options={delayOptions ?? []}
                                onInputChange={(fieldName, newValue) => {
                                    setDelay(newValue);
                                }}
                            />
                        </div>
                    )}

                    {/* For heartbeat type of condition, we will only show the option for select Severity and the user can only select 1 option */}
                    {props.alertConditionType === AlertConditionType.METRIC_HEARTBEAT && (
                        <div className={clsx(classes.basicAttributeInput)}>
                            <div className="label">{capitalizeFirst(trans('alerting.edit_alert_rule.label.condition_severity'))}</div>
                            <ModeSelectInputField
                                className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                                fieldName="severity"
                                editable
                                isInEditMode
                                hideEditableIcon
                                value={conditionInputs.severity ?? ''}
                                error={inputErrors.severity ? trans(inputErrors.severity) : undefined}
                                options={severityOptions ?? []}
                                onInputChange={(fieldName, newValue) => {
                                    setSeverity(newValue);
                                }}
                            />
                        </div>
                    )}


                    {/*
                        For threshold type of condition, we will show the option for select Aggregation and Severity and the user can only
                        multiple severities
                    */}
                    {props.alertConditionType === AlertConditionType.METRIC_THRESHOLD && (
                        <>
                            <div className={clsx(classes.basicAttributeInput)}>
                                <div className="label">{capitalizeFirst(trans('alerting.edit_alert_rule.label.condition_aggregation'))}</div>
                                <div>
                                    <ModeSelectInputField
                                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'select-input')}
                                        fieldName="aggregation"
                                        editable
                                        isInEditMode
                                        hideEditableIcon
                                        value={conditionInputs.aggregation ?? ''}
                                        error={inputErrors.aggregation ? trans(inputErrors.aggregation) : undefined}
                                        options={aggregationOptions ?? []}
                                        onInputChange={(fieldName, newValue) => {
                                            setAggregation(newValue);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={clsx(classes.basicAttributeInput)}>
                                <div className="label">{capitalizeFirst(trans('alerting.edit_alert_rule.label.condition_severity'))}</div>
                                <div>

                                    {/* For each supported Severity, create a checkbox to toggle the thresholdInputs for that severity */}
                                    {alertSeverities.map((severity) => {

                                        // Get the thresholdInputs for the current severity. We can assume alertConditionInputs.thresholds?.[severity]
                                        const thresholdInputs = conditionInputs.thresholds?.[severity];

                                        return (
                                            <FormControlLabel
                                                key={severity}
                                                control={(
                                                    <Checkbox
                                                        key={severity}
                                                        checked={thresholdInputs !== undefined}
                                                        value={thresholdInputs !== undefined}
                                                        color="primary"
                                                        onChange={(event: React.ChangeEvent) => {
                                                            const isChecked = (event.target as HTMLInputElement).checked;
                                                            if (isChecked) {
                                                                addThreshold(severity);
                                                            } else {
                                                                removeThreshold(severity);
                                                            }
                                                        }}
                                                    />
                                                )}
                                                label={capitalizeFirst(trans(getAlertSeverityDisplayName(severity)))}
                                                labelPlacement="end"
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {props.alertConditionType === AlertConditionType.METRIC_THRESHOLD && conditionInputs.thresholds !== undefined && (
                    <>
                        {/*
                            Get the severities that has threshold inputs created for it and then for each of those severity, create the UI to
                            configure the threshold for that severity
                         */}
                        {alertSeverities.filter((severity) => {
                            return conditionInputs.thresholds?.[severity] !== undefined;
                        }).map((severity) => {

                            // Get the thresholdInputs for the current severity. We can assume alertConditionInputs.thresholds?.[severity]
                            // is NOT undefined because we already checked "alertConditionInputs.thresholds !== undefined"
                            // and already filtered out "alertConditionInputs.thresholds[severity] === undefined"
                            const thresholdInputs = conditionInputs.thresholds?.[severity] as AlertConditionThresholdInputs;

                            return (
                                <Paper key={severity} elevation={1} className={clsx(sharedClasses.formControlGroup, classes.thresholdInputFormGroup)}>
                                    <div className={classes.thresholdSettingsTitle}>
                                        <Trans
                                            i18nKey="alerting.edit_alert_rule.step_conditions.metric_threshold.title"
                                        >
                                            <span className={clsx('severity', `severity-${severity}`)}>
                                                {{ severity: capitalizeFirst(trans(getAlertSeverityDisplayName(severity))) } }
                                            </span>
                                        </Trans>
                                    </div>

                                    {/* Operator input */}
                                    <div className={classes.thresholdInputsContainer}>
                                        <ModeSelectInputField
                                            className={clsx(formClasses.formInputField, sharedClasses.formControl, 'form-control operator')}
                                            fieldName="operator"
                                            editable
                                            isInEditMode
                                            hideEditableIcon
                                            value={thresholdInputs.operator ?? ''}
                                            options={thresholdComparisonOperatorOptions ?? []}
                                            onInputChange={(fieldName, newValue) => {
                                                setThresholdOperator(severity, newValue);
                                            }}
                                        />

                                        {/* Create 1 Text input field for each operand in the threshold.operands array */}
                                        {thresholdInputs.operands?.map((operand, index) => {

                                            const label = capitalizeAll(trans(getOperandLabelsByOperatorType(thresholdInputs.operator, index)));

                                            return (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <Fragment key={index}>
                                                    {index > 0 && (
                                                        <span className="conjunction">
                                                            {trans(getOperandsConjunctionByOperatorType(thresholdInputs.operator))}
                                                        </span>
                                                    )}
                                                    <ModeTextInputField
                                                        fieldName="name"
                                                        className={clsx(
                                                            formClasses.formInputField, sharedClasses.formControl, 'form-control operand',
                                                        )}
                                                        inputType="number"
                                                        required
                                                        editable
                                                        isInEditMode
                                                        hideEditableIcon
                                                        label={targetMetric?.unit ? `${label} (${targetMetric.unit})` : label}
                                                        value={thresholdInputs.operands?.[index] ?? ''}
                                                        onInputChange={(fieldName, newValue) => {
                                                            setThresholdOperandValue(severity, index, newValue);
                                                        }}
                                                    />
                                                </Fragment>
                                            );
                                        })}

                                        {/* Show the Add/Remove operand buttons for threshold with operators that support unlimited inputs */}
                                        {operandsLengthByOperatorType[thresholdInputs.operator ?? ''] === 0 && (
                                            <>
                                                {/* Button to remove operand. Disabled if there is only 1 operand left */}
                                                <IconButton
                                                    className="add-threshold-button"
                                                    size="small"
                                                    color="secondary"
                                                    disabled={thresholdInputs.operands && thresholdInputs.operands.length <= 1}
                                                    onClick={() => {
                                                        decreaseThresholdOperandsSize(severity);
                                                    }}
                                                >
                                                    <Icon fontSize="small">remove</Icon>
                                                </IconButton>

                                                {/* Button to add operand. Disabled if there is already have MAX operands */}
                                                <IconButton
                                                    className="add-threshold-button"
                                                    size="small"
                                                    color="primary"
                                                    disabled={thresholdInputs.operands
                                                                && thresholdInputs.operands.length >= ALERT_RULE_CONDITION_THRESHOLD_OPERAND_MAX}
                                                    onClick={() => {
                                                        increaseThresholdOperandsSize(severity);
                                                    }}
                                                >
                                                    <Icon fontSize="small">add</Icon>
                                                </IconButton>
                                            </>
                                        )}
                                    </div>

                                    {inputErrors.thresholds?.[severity] && (
                                        <div className={classes.thresholdSettingsError}>
                                            {trans(inputErrors.thresholds?.[severity] ?? '')}
                                        </div>
                                    )}
                                </Paper>
                            );
                        })}
                    </>
                )}
            </div>

            {/* Show error message if 'emptyThresholdError' or there is no threshold configured */}
            {emptyThresholdError && (
                <div className={classes.emptyThresholdError}>
                    {capitalizeFirst(trans('alerting.edit_alert_rule.step_conditions.thresholds.errors.empty'))}
                </div>
            )}

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            submitInputs(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            if (submitInputs()) {
                                props.onNextStepClick();
                            }
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                    {!props.isNewAlertRule && (
                        <Button
                            variant="contained"
                            color="default"
                            size="small"
                            onClick={() => {
                                if (props.onGotoLastStepClick && submitInputs()) {
                                    props.onGotoLastStepClick();
                                }
                            }}
                            className={clsx(sharedClasses.navButton, 'finish')}
                        >
                            {capitalizeAll(trans('label.finish'))}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
